import { AlertTriangle, ChevronRight, Copy, Link2, RotateCw } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import { getDashboardClients } from 'common-src/models/Client';
import {
  AthenaDepartment,
  getPatientIntegrationsAthena,
} from 'common-src/models/PatientIntegrationsAthena';

import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';

import {
  getGateBadgeLabel,
  pathwayLabel,
  POST_VISIT_BADGE_META_BY_STATUS,
  PRIORITY_BADGE_COPY,
  PRIORITY_SORT_ORDER,
  READINESS_BADGE_META_BY_STATUS,
  TASK_PRIORITY,
  VISIT_TYPES,
} from '../../constants';
import {
  athenaUrl,
  buildLinkedTaskStructure,
  buildRenderableSectionItems,
  canMarkTaskComplete,
  copyTextToClipboard,
  daysUntil,
  formatClinicianName,
  formatDue,
  getAppointmentReadinessStatus,
  getClientByAthenaProviderId,
  getLinkedTaskWaitingForTitle,
  getPatient,
  getPostVisitCardStatus,
  getPostVisitTaskDaysRolling,
  getReadinessTaskStatusLabel,
  getTaskDeepLinkUrl,
  getTaskDisplayTitle,
  isLinkedTask,
  isTaskBlockedStatus,
  isTaskCanceled,
  isTaskCanceledForReadiness,
  isTaskEscalatedStatus,
  isTaskReadinessComplete,
  isWorkflowBlockedTask,
} from '../../helpers';
import AthenaLink from '../AthenaLink';
import { Badge } from '../Badge';
import PlatformLink from '../PlatformLink';
import styles from './ReadinessDetail.module.scss';

const getTaskTone = (task) => {
  if (isTaskCanceledForReadiness(task)) return 'canceled';
  if (isTaskReadinessComplete(task)) return 'done';
  if (task?.priority === 'critical' || isTaskBlockedStatus(task)) {
    return 'critical';
  }
  return 'active';
};

const ReadinessTaskRow = ({
  task,
  onSelectTask,
  waitingForTaskTitle,
  isLinkedChild,
  isSubtaskChild,
  linkedChildCount,
  subtaskChildCount,
  linkedExpanded,
  onToggleLinked,
}) => {
  const [isPriorityBadgePulsing, setIsPriorityBadgePulsing] = React.useState(false);
  const { showAlert, AlertType } = useAlert();
  const taskTone = getTaskTone(task);
  const due = formatDue(task?.due);
  const displayStatusLabel = getReadinessTaskStatusLabel(task);
  const claimBy = task?.taskAssignDueAt ? formatDue(task.taskAssignDueAt) : null;
  const isCompleted = isTaskReadinessComplete(task);
  const isCanceledForReadiness = isTaskCanceledForReadiness(task);
  const isBlocked = isTaskBlockedStatus(task) && !isCompleted;
  const isEscalated = isTaskEscalatedStatus(task) && !isCompleted;
  const isPrerequisiteBlockedTask = isLinkedTask(task);
  const isBlockedByDependencies = isWorkflowBlockedTask(task);
  const isHiddenTask = !canMarkTaskComplete(task);
  const taskPriority = task?.priority ?? TASK_PRIORITY.Low;
  const taskTitle = getTaskDisplayTitle(task);
  const showPriorityBorder = !isCompleted;
  const priorityBorderClass = isPrerequisiteBlockedTask
    ? styles.taskRowPriorityLinked
    : styles[`taskRowPriority${taskPriority}`] || '';
  const showPriorityBadge = !isCompleted;
  const showCriticalIcon =
    !isPrerequisiteBlockedTask &&
    !isBlocked &&
    showPriorityBorder &&
    taskPriority === TASK_PRIORITY.Critical;
  const taskDeepLinkUrl = getTaskDeepLinkUrl(task);
  const totalChildCount = linkedChildCount + subtaskChildCount;
  const showLinkedToggle = totalChildCount > 0 && typeof onToggleLinked === 'function';
  const childToggleLabel = [
    linkedChildCount > 0
      ? `${linkedChildCount} linked ${linkedChildCount === 1 ? 'task' : 'tasks'}`
      : null,
    subtaskChildCount > 0
      ? `${subtaskChildCount} sub-${subtaskChildCount === 1 ? 'task' : 'tasks'}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  React.useEffect(() => {
    if (taskPriority !== TASK_PRIORITY.Critical || isCompleted) {
      setIsPriorityBadgePulsing(false);
      return undefined;
    }

    setIsPriorityBadgePulsing(true);
    const timer = setTimeout(() => setIsPriorityBadgePulsing(false), 1000);
    return () => clearTimeout(timer);
  }, [isCompleted, taskPriority, task?.id]);

  const handleCopyTaskLink = async (event) => {
    event.stopPropagation();
    if (!taskDeepLinkUrl) return;

    try {
      const didCopy = await copyTextToClipboard(taskDeepLinkUrl);
      if (!didCopy) throw new Error('Copy failed');
      showAlert(AlertType.Success, '', 'Task link copied to clipboard.');
    } catch (error) {
      showAlert(AlertType.Error, '', 'Unable to copy task link.');
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={`${styles.taskRowWrap} ${styles[`taskRow${taskTone}`] || ''} ${
        showPriorityBorder ? priorityBorderClass : ''
      } ${!isCompleted && isHiddenTask && !isPrerequisiteBlockedTask ? styles.taskRowDisabled : ''} ${
        isLinkedChild || isSubtaskChild ? styles.taskRowChild : ''
      }`}
      onClick={() => onSelectTask?.(task)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelectTask?.(task);
        }
      }}
      title={
        !isCompleted && isHiddenTask && !isPrerequisiteBlockedTask
          ? 'Task can be claimed, but cannot be completed yet'
          : undefined
      }
    >
      {isPrerequisiteBlockedTask ? (
        <span className={styles.prerequisiteIcon} aria-hidden="true">
          <Link2 className={styles.prerequisiteIconGlyph} />
        </span>
      ) : showCriticalIcon ? (
        <span className={styles.criticalIcon} aria-hidden="true">
          <AlertTriangle className={styles.criticalIconGlyph} strokeWidth={2.2} />
        </span>
      ) : (
        <span
          className={`${styles.taskCheck} ${isCompleted ? styles.taskCheckDone : ''} ${
            isCanceledForReadiness ? styles.taskCheckCanceled : ''
          }`}
          aria-hidden="true"
        >
          {isCanceledForReadiness ? '-' : isCompleted ? '✓' : ''}
        </span>
      )}
      <span className={styles.taskContent}>
        <span className={styles.taskMainRow}>
          <span className={styles.taskTitleWrap}>
            <span className={styles.taskTitleRow}>
              <span className={styles.taskTitle}>{taskTitle}</span>
              {isBlocked ? <Badge variant="critical">Blocked</Badge> : null}
              {!isBlocked && isEscalated ? <Badge variant="escalated">Escalated</Badge> : null}
            </span>
          </span>
          <span className={styles.taskRightRail}>
            <span
              className={`${styles.taskDue} ${isCompleted ? styles.taskDueDone : ''} ${
                isCanceledForReadiness ? styles.taskDueCanceled : ''
              } ${due.urgent ? styles.taskDueUrgent : ''}`}
            >
              {isCompleted ? displayStatusLabel : due.text}
            </span>
            {showPriorityBadge ? (
              <>
                <span
                  className={`${styles.priorityBadge} ${
                    styles[`priorityBadge${taskPriority}`] || ''
                  } ${isPriorityBadgePulsing ? styles.priorityBadgePulse : ''}`}
                >
                  {PRIORITY_BADGE_COPY[taskPriority] ?? taskPriority}
                </span>
                <button
                  type="button"
                  className={styles.copyButton}
                  onClick={handleCopyTaskLink}
                  disabled={!taskDeepLinkUrl}
                  aria-label="Copy task link"
                  title="Copy task link"
                >
                  <Copy className={styles.copyIcon} strokeWidth={1.8} />
                </button>
              </>
            ) : null}
          </span>
        </span>
        {isPrerequisiteBlockedTask && waitingForTaskTitle ? (
          <span className={styles.waitingForRow}>
            <span className={styles.waitingForIcon} aria-hidden="true">
              <Link2 className={styles.waitingForIconGlyph} />
            </span>
            <span className={styles.waitingForLabel}>
              {isBlockedByDependencies ? 'Blocked by:' : 'Waiting on:'}
            </span>
            <span className={styles.waitingForTitle}>{waitingForTaskTitle}</span>
          </span>
        ) : null}
        {showLinkedToggle ? (
          <button
            type="button"
            className={styles.linkedToggle}
            onClick={(event) => {
              event.stopPropagation();
              onToggleLinked?.(task.id);
            }}
          >
            <ChevronRight
              className={`${styles.linkedToggleChevron} ${
                linkedExpanded ? styles.linkedToggleChevronOpen : ''
              }`}
              strokeWidth={1.8}
            />
            <Link2 className={styles.linkedToggleIcon} strokeWidth={1.8} />
            <span>{childToggleLabel}</span>
          </button>
        ) : null}
        {!isCompleted && claimBy ? (
          <span className={styles.taskCaption}>Assign by: {claimBy.text}</span>
        ) : null}
        {task.caption ? <span className={styles.taskCaption}>{task.caption}</span> : null}
      </span>
    </div>
  );
};

ReadinessTaskRow.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    assessmentType: PropTypes.string,
    caption: PropTypes.string,
    due: PropTypes.string,
    taskAssignDueAt: PropTypes.string,
    status: PropTypes.number,
    priority: PropTypes.string,
    escalatedStatus: PropTypes.number,
    isVisible: PropTypes.bool,
    dependency: PropTypes.object,
  }),
  onSelectTask: PropTypes.func,
  waitingForTaskTitle: PropTypes.string,
  isLinkedChild: PropTypes.bool,
  isSubtaskChild: PropTypes.bool,
  linkedChildCount: PropTypes.number,
  subtaskChildCount: PropTypes.number,
  linkedExpanded: PropTypes.bool,
  onToggleLinked: PropTypes.func,
};

const departmentNameById = Object.values(AthenaDepartment).reduce((acc, department) => {
  acc[`${department.id}`] = department.value;
  return acc;
}, {});

const formatDepartmentLocation = (departmentAthenaId) =>
  departmentNameById[`${departmentAthenaId ?? ''}`] || '';

const formatWorkflowDueAt = (value) => {
  if (!value || typeof value !== 'string') return null;

  const normalizedValue = value.trim();
  const isoLikeMatch = normalizedValue.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2})/);
  if (isoLikeMatch) {
    return `${isoLikeMatch[1]} · ${isoLikeMatch[2]}`;
  }

  const parsedValue = new Date(normalizedValue);
  if (Number.isNaN(parsedValue.getTime())) return null;

  return parsedValue.toISOString().slice(0, 16).replace('T', ' · ');
};

const getAppointmentPathway = (appointmentTasks = [], patientPathway = null) => {
  const pathwayCounts = appointmentTasks.reduce((acc, task) => {
    const pathway = task?.pathway;
    if (!pathway) return acc;
    acc[pathway] = (acc[pathway] ?? 0) + 1;
    return acc;
  }, {});

  const topPathway = Object.entries(pathwayCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
  return topPathway ?? patientPathway ?? null;
};

const formatVisitAgoLabel = (days) => {
  if (!Number.isFinite(days)) return null;
  if (days === 0) return 'today';
  if (days === -1) return '1d ago';
  if (days < 0) return `${Math.abs(days)}d ago`;
  return `in ${days}d`;
};

const ReadinessDetail = ({
  appt,
  onClose,
  onSelectTask,
  appointmentTasks = [],
  delegatedSubtasksByParentId = {},
  isPostVisit = false,
  onDismiss,
}) => {
  if (!appt) return null;
  const [expandedLinkedTasks, setExpandedLinkedTasks] = React.useState({});

  const sourceAppointmentId = appt?.sourceAppointmentId ?? appt?.id;
  const patient = appt?.patient ?? getPatient(appt?.patientId);
  const visitType = VISIT_TYPES[appt.visitType];
  const workflowDueAt = appt?.workflowDueAt ? new Date(appt.workflowDueAt) : null;
  const workflowDueDateOnly =
    workflowDueAt && !Number.isNaN(workflowDueAt.getTime())
      ? workflowDueAt.toISOString().slice(0, 10)
      : null;
  const workflowDueDateText =
    workflowDueAt && !Number.isNaN(workflowDueAt.getTime())
      ? formatWorkflowDueAt(appt.workflowDueAt)
      : null;
  const days = workflowDueDateOnly ? daysUntil(workflowDueDateOnly) : null;
  const readinessTasks = React.useMemo(() => {
    const tasks = appointmentTasks.filter(Boolean);

    return [...tasks].sort((a, b) => {
      const aCompleted = isTaskReadinessComplete(a);
      const bCompleted = isTaskReadinessComplete(b);
      if (aCompleted !== bCompleted) return aCompleted ? -1 : 1;

      if (!aCompleted && !bCompleted) {
        const aLinked = isLinkedTask(a);
        const bLinked = isLinkedTask(b);
        if (aLinked !== bLinked) return aLinked ? 1 : -1;
      }

      const priorityDiff =
        (PRIORITY_SORT_ORDER[a?.priority] ?? Number.POSITIVE_INFINITY) -
        (PRIORITY_SORT_ORDER[b?.priority] ?? Number.POSITIVE_INFINITY);
      if (priorityDiff !== 0) return priorityDiff;

      const aDue = a?.due ? new Date(a.due).getTime() : Number.POSITIVE_INFINITY;
      const bDue = b?.due ? new Date(b.due).getTime() : Number.POSITIVE_INFINITY;
      if (aDue !== bDue) return aDue - bDue;

      return `${a?.title || ''}`.localeCompare(`${b?.title || ''}`);
    });
  }, [appointmentTasks]);
  const activeReadinessTasks = React.useMemo(
    () => readinessTasks.filter((task) => !isTaskCanceledForReadiness(task)),
    [readinessTasks],
  );
  const canceledReadinessTasks = React.useMemo(
    () => readinessTasks.filter((task) => isTaskCanceledForReadiness(task)),
    [readinessTasks],
  );
  const linkedTaskStructure = React.useMemo(
    () => buildLinkedTaskStructure(activeReadinessTasks, sourceAppointmentId),
    [activeReadinessTasks, sourceAppointmentId],
  );
  const renderableReadinessItems = React.useMemo(
    () =>
      buildRenderableSectionItems(
        activeReadinessTasks,
        linkedTaskStructure,
        expandedLinkedTasks,
        delegatedSubtasksByParentId,
      ),
    [activeReadinessTasks, delegatedSubtasksByParentId, expandedLinkedTasks, linkedTaskStructure],
  );
  const delegatedSubtaskCountByParentId = React.useMemo(
    () =>
      activeReadinessTasks.reduce((acc, readinessTask) => {
        const parentId = `${readinessTask?.id || ''}`.trim();
        if (!parentId) return acc;

        const uniqueSubtasks = new Map();
        const localSubtasks = linkedTaskStructure.subtaskChildTasksByParentId.get(parentId) || [];
        const hydratedSubtasks = delegatedSubtasksByParentId[parentId] || [];

        [...hydratedSubtasks, ...localSubtasks].forEach((subtask) => {
          const subtaskId = `${subtask?.id || ''}`.trim();
          if (!subtaskId || isTaskCanceled(subtask)) return;
          uniqueSubtasks.set(subtaskId, subtask);
        });

        acc[parentId] = uniqueSubtasks.size;
        return acc;
      }, {}),
    [activeReadinessTasks, delegatedSubtasksByParentId, linkedTaskStructure],
  );
  const completedTasks = readinessTasks.filter((task) => isTaskReadinessComplete(task));
  const completionPercent = readinessTasks.length
    ? Math.round((completedTasks.length / readinessTasks.length) * 100)
    : 100;
  const readinessStatus = getAppointmentReadinessStatus(activeReadinessTasks);
  const readinessBadgeMeta = READINESS_BADGE_META_BY_STATUS[readinessStatus] ?? null;
  const gateBadgeLabel = getGateBadgeLabel(days);
  const postVisitStatus = isPostVisit ? getPostVisitCardStatus(activeReadinessTasks) : null;
  const postVisitBadgeMeta = POST_VISIT_BADGE_META_BY_STATUS[postVisitStatus] ?? null;
  const postVisitOpenTasks = activeReadinessTasks.filter((task) => !isTaskReadinessComplete(task));
  const postVisitRollingCount = isPostVisit ? postVisitOpenTasks.length : 0;
  const postVisitOldestRollingDays = postVisitRollingCount
    ? Math.max(...postVisitOpenTasks.map(getPostVisitTaskDaysRolling))
    : 0;
  const visitDate = appt?.departmentLocalDate || appt?.date;
  const visitAgoLabel = isPostVisit
    ? formatVisitAgoLabel(visitDate ? daysUntil(visitDate, appt?.departmentTimezone) : NaN)
    : null;
  const integrations = useCustomSelector((state) =>
    patient?.id ? getPatientIntegrationsAthena(state, patient.id) : [],
  );
  const clients = useCustomSelector((state) => getDashboardClients(state)) || [];
  const athenaIntegration = integrations[0] ?? null;
  const athenaLink = athenaUrl(athenaIntegration?.athenaPatientId, athenaIntegration?.practiceId);
  const athenaPatientId = athenaIntegration?.athenaPatientId || patient?.athenaId || '-';
  const department = athenaIntegration?.department || '-';
  const clinicianClient = getClientByAthenaProviderId(clients, appt?.providerAthenaId);
  const clinician = formatClinicianName(clinicianClient);
  const pathway = getAppointmentPathway(readinessTasks, patient?.pathway);
  const location = formatDepartmentLocation(appt?.departmentAthenaId) || appt.location || '-';
  const waitingForTaskTitleById = React.useMemo(
    () =>
      readinessTasks.reduce((acc, task) => {
        if (task?.isVisible !== false) return acc;

        acc[task.id] = getLinkedTaskWaitingForTitle(task, readinessTasks, sourceAppointmentId);
        return acc;
      }, {}),
    [readinessTasks, sourceAppointmentId],
  );
  const toggleLinkedTasks = React.useCallback((taskId) => {
    const normalizedTaskId = `${taskId || ''}`;
    if (!normalizedTaskId) return;

    setExpandedLinkedTasks((prev) => ({
      ...prev,
      [normalizedTaskId]: !prev[normalizedTaskId],
    }));
  }, []);

  return (
    <div className={styles.root}>
      <div className={styles.sectionHeader}>
        <div className={styles.headerTop}>
          <div>
            <div className={styles.patientName}>{patient?.name}</div>
            <div className={styles.visitType}>{visitType?.label}</div>
          </div>
          <button type="button" onClick={onClose} className={styles.closeButton}>
            ×
          </button>
        </div>
        <div className={styles.badgesRow}>
          {isPostVisit ? (
            <>
              {postVisitBadgeMeta ? (
                <Badge variant={postVisitBadgeMeta.variant}>{postVisitBadgeMeta.label}</Badge>
              ) : null}
              {visitAgoLabel ? <Badge variant="outline">{`Visit ${visitAgoLabel}`}</Badge> : null}
              {workflowDueDateText ? <Badge variant="outline">{workflowDueDateText}</Badge> : null}
              {postVisitRollingCount > 0 ? (
                <Badge variant={postVisitStatus === 'red' ? 'red' : 'yellow'}>
                  <RotateCw size={10} className={styles.rollingBadgeIcon} aria-hidden="true" />
                  {`${postVisitRollingCount} rolling${
                    postVisitOldestRollingDays > 0 ? ` · oldest ${postVisitOldestRollingDays}d` : ''
                  }`}
                </Badge>
              ) : null}
            </>
          ) : (
            <>
              {readinessBadgeMeta ? (
                <Badge variant={readinessBadgeMeta.variant}>{readinessBadgeMeta.label}</Badge>
              ) : null}
              {workflowDueDateText ? <Badge variant="outline">{workflowDueDateText}</Badge> : null}
              {gateBadgeLabel ? <Badge variant="outline">{gateBadgeLabel}</Badge> : null}
            </>
          )}
        </div>
        {visitType?.description && (
          <div className={styles.visitDescription}>{visitType.description}</div>
        )}
      </div>

      <div className={styles.sectionInfo}>
        <div className={styles.infoGrid}>
          {[
            ['Clinician', clinician],
            ['Athena ID', athenaPatientId],
            ['Department', department],
            ['Location', location],
            ['Pathway', pathwayLabel[pathway] ?? pathway ?? '-'],
            // TODO: Remove this demo fallback value after the demo.
            ['Phase', patient?.phase ?? '-'],
          ].map(([key, value], index) => (
            <div key={index} className={styles.infoRow}>
              <span className={styles.infoLabel}>{key}</span>
              <span className={styles.infoValue}>{value}</span>
            </div>
          ))}
        </div>
        {(patient || appt?.patientId) && (
          <div className={styles.linkWrap}>
            <PlatformLink patient={patient} patientId={appt?.patientId} />
          </div>
        )}
        {patient && (
          <div className={styles.linkWrap}>
            <AthenaLink patient={patient} integration={athenaIntegration} />
          </div>
        )}
      </div>

      <div className={styles.sectionProgress}>
        <div className={styles.progressHeader}>
          <span className={styles.sectionLabel}>
            {isPostVisit ? 'Post-visit follow-up' : 'Readiness'}
          </span>
          <span
            className={styles.progressMeta}
          >{`${completedTasks.length}/${readinessTasks.length} done`}</span>
        </div>
        <div className={styles.progressTrack}>
          <div
            className={`${styles.progressFill} ${
              completedTasks.length === readinessTasks.length
                ? styles.progressFillDone
                : styles.progressFillActive
            }`}
            style={{ width: `${completionPercent}%` }}
          />
        </div>
      </div>

      <div className={styles.sectionNeeds}>
        <div className={styles.tasksSectionHeader}>
          <div className={styles.sectionLabel}>Tasks</div>
          <div className={styles.tasksSummary}>
            {readinessTasks.length === 0
              ? 'No tasks'
              : completedTasks.length === readinessTasks.length
                ? 'All done'
                : `${readinessTasks.length - completedTasks.length} remaining`}
          </div>
        </div>
        {readinessTasks.length > 0 ? (
          <div className={styles.tasksBox}>
            {renderableReadinessItems.map((item) => {
              if (item.type === 'child-group') {
                return (
                  <div key={item.key} className={styles.childGroupContainer}>
                    {item.items.map((childItem) => {
                      if (childItem.type === 'label') {
                        return (
                          <div key={childItem.key} className={styles.childSectionLabel}>
                            {childItem.label}
                          </div>
                        );
                      }

                      const { task } = childItem;

                      return (
                        <ReadinessTaskRow
                          key={task.id}
                          task={task}
                          onSelectTask={onSelectTask}
                          waitingForTaskTitle={waitingForTaskTitleById[task.id] ?? null}
                          isLinkedChild={linkedTaskStructure.linkedChildIds.has(`${task.id}`)}
                          isSubtaskChild={linkedTaskStructure.subtaskChildIds.has(`${task.id}`)}
                          isSubtask={!!task?.parentTaskId}
                          parentTitle={
                            task?.parentTaskId
                              ? (activeReadinessTasks.find(
                                  (candidateTask) =>
                                    `${candidateTask?.id || ''}` === `${task.parentTaskId || ''}`,
                                )?.title ?? null)
                              : null
                          }
                          linkedChildCount={
                            linkedTaskStructure.parentLinkedChildCount.get(`${task.id}`) ?? 0
                          }
                          subtaskChildCount={Math.max(
                            isTaskReadinessComplete(task) ? 0 : 0,
                            delegatedSubtaskCountByParentId[`${task.id}`] ?? 0,
                          )}
                          linkedExpanded={!!expandedLinkedTasks[`${task.id}`]}
                          onToggleLinked={toggleLinkedTasks}
                        />
                      );
                    })}
                  </div>
                );
              }

              const { task } = item;

              return (
                <ReadinessTaskRow
                  key={task.id}
                  task={task}
                  onSelectTask={onSelectTask}
                  waitingForTaskTitle={waitingForTaskTitleById[task.id] ?? null}
                  isLinkedChild={linkedTaskStructure.linkedChildIds.has(`${task.id}`)}
                  isSubtaskChild={linkedTaskStructure.subtaskChildIds.has(`${task.id}`)}
                  isSubtask={!!task?.parentTaskId}
                  parentTitle={
                    task?.parentTaskId
                      ? (activeReadinessTasks.find(
                          (candidateTask) =>
                            `${candidateTask?.id || ''}` === `${task.parentTaskId || ''}`,
                        )?.title ?? null)
                      : null
                  }
                  linkedChildCount={
                    linkedTaskStructure.parentLinkedChildCount.get(`${task.id}`) ?? 0
                  }
                  subtaskChildCount={Math.max(
                    isTaskReadinessComplete(task) ? 0 : 0,
                    delegatedSubtaskCountByParentId[`${task.id}`] ?? 0,
                  )}
                  linkedExpanded={!!expandedLinkedTasks[`${task.id}`]}
                  onToggleLinked={toggleLinkedTasks}
                />
              );
            })}
            {canceledReadinessTasks.length > 0 ? (
              <div className={styles.canceledTasksSection}>
                {canceledReadinessTasks.map((task) => (
                  <ReadinessTaskRow
                    key={task.id}
                    task={task}
                    onSelectTask={onSelectTask}
                    waitingForTaskTitle={waitingForTaskTitleById[task.id] ?? null}
                    isLinkedChild={false}
                    isSubtaskChild={false}
                    linkedChildCount={0}
                    subtaskChildCount={0}
                    linkedExpanded={false}
                    onToggleLinked={toggleLinkedTasks}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <div className={styles.emptyTasks}>No tasks for this appointment.</div>
        )}
      </div>

      <div className={styles.sectionActions}>
        {isPostVisit ? (
          onDismiss ? (
            <div className={styles.actionsRow}>
              <button
                type="button"
                className={styles.hideButton}
                onClick={() => onDismiss(appt?.id)}
              >
                Hide from post-visit view
              </button>
            </div>
          ) : null
        ) : (
          <div className={styles.actionsRow}>
            <a
              href={athenaLink || undefined}
              target={athenaLink ? '_blank' : undefined}
              rel={athenaLink ? 'noopener noreferrer' : undefined}
              className={`${styles.rescheduleLink} ${athenaLink ? '' : styles.disabledLink}`}
              aria-disabled={!athenaLink}
              onClick={(e) => {
                if (!athenaLink) e.preventDefault();
              }}
            >
              Reschedule in Athena ↗
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

ReadinessDetail.propTypes = {
  appt: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sourceAppointmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    patient: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      pathway: PropTypes.string,
      phase: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    visitType: PropTypes.string,
    workflowDueAt: PropTypes.string,
    date: PropTypes.string,
    departmentLocalDate: PropTypes.string,
    departmentTimezone: PropTypes.string,
    time: PropTypes.string,
    departmentAthenaId: PropTypes.string,
    providerAthenaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    location: PropTypes.string,
  }),
  onClose: PropTypes.func,
  onSelectTask: PropTypes.func,
  appointmentTasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      assessmentType: PropTypes.string,
      caption: PropTypes.string,
      due: PropTypes.string,
      status: PropTypes.number,
      priority: PropTypes.string,
      isVisible: PropTypes.bool,
    }),
  ),
  delegatedSubtasksByParentId: PropTypes.object,
  isPostVisit: PropTypes.bool,
  onDismiss: PropTypes.func,
};

export default ReadinessDetail;
